import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, LogOut, RefreshCw } from "lucide-react";
import AdminArticles from "./AdminArticles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "jas_admin_token";

const Admin = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [passkey, setPasskey] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [enquiries, setEnquiries] = useState(null);
  const [tab, setTab] = useState("enquiries");

  const load = useCallback(
    async (t) => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/enquiries`, {
          headers: { Authorization: `Bearer ${t}` },
        });
        setEnquiries(data.enquiries);
        setError("");
      } catch {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setEnquiries(null);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { passkey });
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      setError(
        err.response?.status === 429
          ? "Too many attempts. Please wait 15 minutes."
          : "Incorrect passkey."
      );
      setLoading(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setEnquiries(null);
    setPasskey("");
  };

  const inputCls =
    "w-full border border-white/10 bg-[#0B0F17] px-4 py-3.5 text-sm font-light text-slate-200 placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-[#F26122]/60";

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B0F17] px-4">
        <form
          onSubmit={login}
          data-testid="admin-login-form"
          className="w-full max-w-sm border border-white/10 bg-[#0D1220] p-8"
        >
          <div className="flex items-center gap-3">
            <Lock className="h-4 w-4 text-[#F2824F]" />
            <h1 className="font-display text-2xl font-light text-slate-100">
              Enquiry Review
            </h1>
          </div>
          <p className="mt-2 text-xs font-light text-slate-500">
            Jeet Advisory Services — private area
          </p>
          <input
            data-testid="admin-passkey-input"
            type="password"
            value={passkey}
            onChange={(e) => setPasskey(e.target.value)}
            placeholder="Admin passkey"
            className={`${inputCls} mt-8`}
            required
          />
          {error && (
            <p data-testid="admin-login-error" className="mt-3 text-xs text-red-400">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="mt-6 w-full bg-[#F26122] px-6 py-3.5 text-sm font-medium text-[#0B0F17] transition-colors duration-300 hover:bg-[#FF7B3D] disabled:opacity-60"
          >
            {loading ? "Checking…" : "View Enquiries"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F17] px-4 py-12 sm:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display text-3xl font-light text-slate-100">
              Admin
            </h1>
            <p className="mt-1 font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500">
              Jeet Advisory Services — private area
            </p>
          </div>
          <div className="flex gap-3">
            {tab === "enquiries" && (
              <button
                data-testid="admin-refresh-button"
                onClick={() => load(token)}
                className="flex items-center gap-2 border border-white/15 px-4 py-2.5 text-xs text-slate-300 transition-colors hover:border-[#F26122]/60 hover:text-[#F26122]"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Refresh
              </button>
            )}
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 border border-white/15 px-4 py-2.5 text-xs text-slate-300 transition-colors hover:border-red-400/60 hover:text-red-400"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>

        <div className="mt-8 inline-flex gap-px border border-white/10 bg-white/10">
          {[
            ["enquiries", "Enquiries"],
            ["articles", "Articles"],
          ].map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-6 py-3 text-xs tracking-wide transition-colors duration-300 ${
                tab === key
                  ? "bg-[#F26122] font-medium text-[#0B0F17]"
                  : "bg-[#0D1220] text-slate-400 hover:text-[#F26122]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "articles" && <AdminArticles token={token} />}

        {tab === "enquiries" &&
          (loading && !enquiries ? (
            <p className="mt-16 text-center text-sm text-slate-500">Loading…</p>
          ) : !enquiries?.length ? (
            <p
              data-testid="admin-enquiries-empty"
              className="mt-16 text-center text-sm text-slate-500"
            >
              No enquiries yet.
            </p>
          ) : (
            <div data-testid="admin-enquiries-list" className="mt-10 space-y-px border border-white/10 bg-white/10">
              {enquiries.map((q) => (
                <article
                  key={q.id}
                  data-testid={`admin-enquiry-${q.id}`}
                  className="bg-[#0D1220] p-6"
                >
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h2 className="font-display text-xl text-slate-100">
                      {q.name}
                      {q.company && (
                        <span className="ml-2 text-sm text-slate-400">
                          {q.designation ? `${q.designation}, ` : ""}
                          {q.company}
                        </span>
                      )}
                    </h2>
                    <span className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                      {q.created_at
                        ? new Date(q.created_at).toLocaleString("en-IN", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : ""}
                    </span>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-400">
                    <span>{q.email}</span>
                    <span>{q.phone}</span>
                    {q.funding_requirement && <span>{q.funding_requirement}</span>}
                    {q.purpose && <span>{q.purpose}</span>}
                    {q.preferred_contact && (
                      <span className="text-[#F2824F]">
                        Prefers {q.preferred_contact}
                      </span>
                    )}
                  </div>
                  {q.description && (
                    <p className="mt-3 text-sm font-light leading-relaxed text-slate-400">
                      {q.description}
                    </p>
                  )}
                </article>
              ))}
            </div>
          ))}
      </div>
    </div>
  );
};

export default Admin;
